/**
 * API helpers for the LTI Tools tab.
 *
 * All requests go through the sbl-assistant-api-proxy Django application,
 * which adds edX JWT / IsStaff authentication before forwarding to phoenix-assistant-sbl.
 *
 * Base path: /assistant-proxy/api/v1/lti-configs
 */
import { getAuthenticatedHttpClient } from '@edx/frontend-platform/auth';
import { getConfig } from '@edx/frontend-platform';

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export interface LtiConfig {
  config_id: string;
  course_id: string;
  skill: string;
  lti_type: 'checkin' | 'pre_assessment' | 'post_assessment';
  scenario_id: string;
  scenario_text: string;
  response_prompt: string;
  rubric: SubRubricDimension[];
  max_attempts: number;
  placement_hint?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface StudioBlock {
  config_id: string;
  tool_url: string;
  consumer_key: string;
  custom_parameters: string;
  studio_block: string;
  // Report LTI block (no custom parameters — LMS passes learner/course context automatically)
  report_tool_url: string;
  report_consumer_key: string;
  report_custom_parameters: string;
}

// ---------------------------------------------------------------------------
// AI Wizard types
// ---------------------------------------------------------------------------

export interface SubRubricDimension {
  name: string;
  weight: number; // 0.0–1.0 fractional (as returned by the API)
  observable_criteria?: string[];
  course_concepts?: CourseConcept[];
  source_excerpts?: SourceExcerpt[];
}

export interface CourseConcept {
  name: string;
  definition: string;
}

export interface SourceExcerpt {
  text: string;
  sequential_id: string;
  vertical_id: string;
}

export interface SkillSection {
  section_index: number;
  section_name: string;
  sequential_id: string;
  skill_name: string;
  sub_rubric: SubRubricDimension[];
  unit_summaries: string[];
  placement_hint: string;
}

export interface CourseAnalysis {
  course_id: string;
  course_skill_name: string;
  skill_sections: SkillSection[];
  top_level_rubric: SubRubricDimension[];
}

export interface ScenarioOption {
  scenario_text: string;
  response_prompt: string;
}

export interface CheckinScenario {
  section_index: number;
  skill_name: string;
  scenario: ScenarioOption;
}

export interface ScenariosResult {
  assessment_scenario: ScenarioOption;
  checkin_scenarios: CheckinScenario[];
}

export interface WizardJob<T> {
  job_id: string;
  operation: 'analyze' | 'scenarios';
  status: 'queued' | 'analyzing' | 'generating' | 'ready' | 'error';
  label: string;
  result?: T;
  error?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const baseUrl = () => {
  const lmsBaseUrl = getConfig().LMS_BASE_URL as string;
  return `${lmsBaseUrl}/assistant-proxy/api/v1/lti-configs`;
};

const aiBaseUrl = () => `${baseUrl()}/ai`;

// ---------------------------------------------------------------------------
// Read-only config API calls
// ---------------------------------------------------------------------------

/** List all LTI tool configurations for a given course. */
export const listLtiConfigs = async (courseId: string): Promise<LtiConfig[]> => {
  const { data } = await getAuthenticatedHttpClient().get(
    `${baseUrl()}?course_id=${encodeURIComponent(courseId)}`,
  );
  if (Array.isArray(data)) {
    return data as LtiConfig[];
  }
  return (data.configs ?? []) as LtiConfig[];
};

/** Delete an LTI tool configuration by its ID. */
export const deleteLtiConfig = async (configId: string): Promise<void> => {
  await getAuthenticatedHttpClient().delete(`${baseUrl()}/${configId}`);
};

/** Delete every LTI configuration for a course so it can be configured again. */
export const deleteLtiConfigsForCourse = async (courseId: string): Promise<void> => {
  await getAuthenticatedHttpClient().delete(`${baseUrl()}?course_id=${encodeURIComponent(courseId)}`);
};

/** Partially update an existing LTI config. Only supplied fields are written. */
export const updateLtiConfig = async (
  configId: string,
  updates: {
    skill?: string;
    scenario_text?: string;
    response_prompt?: string;
    rubric?: SubRubricDimension[];
    max_attempts?: number;
  },
): Promise<LtiConfig[]> => {
  const { data } = await getAuthenticatedHttpClient().put(`${baseUrl()}/${configId}`, updates);
  if (data.configs) {
    return Object.values(data.configs) as LtiConfig[];
  }
  return [data.config ?? data] as LtiConfig[];
};

/** Get the Studio-ready copy-paste configuration block. */
export const getStudioBlock = async (configId: string): Promise<StudioBlock> => {
  const lmsBaseUrl = getConfig().LMS_BASE_URL as string;
  const url = `${lmsBaseUrl}/assistant-proxy/api/v1/lti-configs/studio-block/${configId}`;
  const { data } = await getAuthenticatedHttpClient().get(url);
  return data as StudioBlock;
};

// ---------------------------------------------------------------------------
// Internal helper used only by bulkCreateLtiConfigs
// ---------------------------------------------------------------------------

const createLtiConfig = async (payload: {
  course_id: string;
  skill: string;
  lti_type: string;
  scenario_id: string;
  scenario_text: string;
  response_prompt: string;
  rubric: SubRubricDimension[];
  max_attempts: number;
  placement_hint?: string;
}): Promise<LtiConfig> => {
  const { data } = await getAuthenticatedHttpClient().post(baseUrl(), payload);
  return (data.config ?? data) as LtiConfig;
};

// ---------------------------------------------------------------------------
// AI Wizard API calls
// ---------------------------------------------------------------------------

/**
 * Step 1 — Analyze course structure.
 * Queues course analysis. Poll getWizardJob() with the returned job ID for the result.
 */
export const analyzeCourse = async (courseId: string): Promise<WizardJob<CourseAnalysis>> => {
  const { data } = await getAuthenticatedHttpClient().post(
    `${aiBaseUrl()}/analyze-course`,
    { course_id: courseId },
  );
  return data as WizardJob<CourseAnalysis>;
};

/**
 * Step 2 — Generate scenarios.
 * Queues N+1 parallel LLM calls. Poll getWizardJob() with the returned job ID for results.
 */
export const generateScenarios = async (analysis: CourseAnalysis): Promise<WizardJob<ScenariosResult>> => {
  const { data } = await getAuthenticatedHttpClient().post(
    `${aiBaseUrl()}/generate-scenarios`,
    analysis,
  );
  return data as WizardJob<ScenariosResult>;
};

export const getWizardJob = async <T>(jobId: string): Promise<WizardJob<T>> => {
  const { data } = await getAuthenticatedHttpClient().get(
    `${aiBaseUrl()}/jobs/${jobId}`,
  );
  return data as WizardJob<T>;
};

/**
 * Step 3 — Bulk-create all configs from a completed wizard run.
 * Creates: 1 pre-assessment + N check-ins + 1 post-assessment.
 * Rubric weights are passed as-is (0.0–1.0 fractions from the AI response).
 * Returns array of saved LtiConfig objects in that order.
 */
export const bulkCreateLtiConfigs = async (
  courseId: string,
  analysis: CourseAnalysis,
  scenarios: ScenariosResult,
  maxAttempts: number = 3,
): Promise<LtiConfig[]> => {
  const slugify = (text: string) => text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();

  const saved: LtiConfig[] = [];

  // Pre-assessment
  const preConfig = await createLtiConfig({
    course_id: courseId,
    skill: analysis.course_skill_name,
    lti_type: 'pre_assessment',
    scenario_id: `${slugify(analysis.course_skill_name)}-pre-01`,
    scenario_text: scenarios.assessment_scenario.scenario_text,
    response_prompt: scenarios.assessment_scenario.response_prompt,
    rubric: analysis.top_level_rubric.map((d) => ({ ...d })),
    max_attempts: 1,
  });
  saved.push(preConfig);

  // Check-ins (in order) — use Promise.all to avoid await-in-loop lint error
  const checkinPayloads = analysis.skill_sections
    .map((sec) => {
      const matching = scenarios.checkin_scenarios.find(
        (cs) => cs.section_index === sec.section_index,
      );
      if (!matching) { return null; }
      return {
        course_id: courseId,
        skill: sec.skill_name,
        lti_type: 'checkin' as const,
        scenario_id: `${slugify(sec.skill_name)}-checkin-${String(sec.section_index + 1).padStart(2, '0')}`,
        scenario_text: matching.scenario.scenario_text,
        response_prompt: matching.scenario.response_prompt,
        rubric: sec.sub_rubric.map((d) => ({ ...d })),
        max_attempts: maxAttempts,
        placement_hint: sec.placement_hint,
      };
    })
    .filter((p): p is NonNullable<typeof p> => p !== null);

  const checkins = await Promise.all(checkinPayloads.map((p) => createLtiConfig(p)));
  saved.push(...checkins);

  // Post-assessment
  const postConfig = await createLtiConfig({
    course_id: courseId,
    skill: analysis.course_skill_name,
    lti_type: 'post_assessment',
    scenario_id: `${slugify(analysis.course_skill_name)}-post-01`,
    scenario_text: scenarios.assessment_scenario.scenario_text,
    response_prompt: scenarios.assessment_scenario.response_prompt,
    rubric: analysis.top_level_rubric.map((d) => ({ ...d })),
    max_attempts: 1,
  });
  saved.push(postConfig);

  return saved;
};
